import { FrameworkId } from "./frameworkId";
import { EmployeeSocialFactor, EmploymentLevel, Gender, OccupationType } from "../../entities/contractorEmployee";
import { EmissionFactorScope } from "../../entities/emissionFactor";
import { DIVERTED_WASTE_DESTINATIONS, EcoLabel, EnvironmentalCategory, EnvironmentalSubCategory, EnvironmentalTargetType } from "../../entities/environmentalCommitment";
import { EnvironmentalTargetMeasure, EnvironmentalTargetMeasuredBy, EnvironmentalTargetMethod } from "../../entities/environmentalTarget";
import { LocalContentType } from "../../entities/packageLocalContent";
import { PolicyFrequency, PolicyType } from "../../entities/projectPolicy";
import { SocialSpendCertificate, SocialSpendEntityType } from "../../entities/socialSpend";
import { SocialCommitmentTypeSpend } from "../packageSocialSpendCommitment";
import { LabourHourFactorCountingMethod, LabourHourTargetDenominator, ProjectLabourHourCommitmentType } from "../projectLabourHourCommitment";
export var FrameworkTargetCategory;
(function (FrameworkTargetCategory) {
    FrameworkTargetCategory["Hours"] = "framework-target-category_hours";
    FrameworkTargetCategory["Spend"] = "framework-target-category_spend";
    FrameworkTargetCategory["LocalContent"] = "framework-target-category_local-content";
    FrameworkTargetCategory["Environmental"] = "framework-target-category_environmental";
    FrameworkTargetCategory["Documents"] = "framework-target-category_documents";
})(FrameworkTargetCategory || (FrameworkTargetCategory = {}));
// The three bodies that certify an Indigenous business. Named for what it is
// rather than for the first framework that needed it — the Green Star and IS
// First Nations spend targets take the same list.
const indigenousBusinessCertifications = [
    SocialSpendCertificate.Kinaway,
    SocialSpendCertificate.SupplyNationCertified,
    SocialSpendCertificate.SupplyNationRegistered
];
/**
 * Share of C&D tonnes sent to a diverted destination (Recycled or Reuse, as on
 * the waste dashboards). 80% at Minimum Expectation, 90% for the point.
 */
const wasteDiversion = (id, targetValue) => ({
    category: EnvironmentalCategory.ConstructionDemolitionWaste,
    id,
    measuredBy: EnvironmentalTargetMeasuredBy.DestinationShare,
    targetCategory: FrameworkTargetCategory.Environmental,
    targetMethod: EnvironmentalTargetMethod.Percentage,
    targetName: "C&D Waste Diverted from Landfill",
    targetType: EnvironmentalTargetType.Waste,
    targetValue,
    wasteDisposalDestinations: [...DIVERTED_WASTE_DESTINATIONS],
    // GBCA excludes special waste (Buildings v1.1 p.48). Excavation needs no
    // filter: it files under Spoil / Fill / Soil, outside C&D.
    wasteHazardous: false
});
// Green Star "verified supplier spend". BuyAbility has no
// `SocialSpendCertificate` yet, so a disability enterprise is admitted by
// entity type but cannot be verified (#34).
const verifiedSupplierCertifications = [
    SocialSpendCertificate.SupplyNationCertified,
    SocialSpendCertificate.SupplyNationRegistered,
    SocialSpendCertificate.SocialTraders,
    SocialSpendCertificate.Kinaway
];
const verifiedSupplierEntityTypes = [
    SocialSpendEntityType.IndigenousEnterprise,
    SocialSpendEntityType.SocialEnterprise,
    SocialSpendEntityType.DisabilityEnterprise
];
const vicAtcEmploymentLevels = [
    EmploymentLevel.Apprentice,
    EmploymentLevel.Trainee,
    EmploymentLevel.Cadet
];
export const frameworksList = [
    {
        country: "AU",
        frameworkAbbreviation: "IPP",
        frameworkId: FrameworkId.NAT_1,
        frameworkName: "Commonwealth Indigenous Procurement Policy (IPP) (MMR) 2025",
        targets: [
            // Hours
            {
                employmentLevels: [
                    EmploymentLevel.FullEmployment,
                    EmploymentLevel.Apprentice,
                    EmploymentLevel.Trainee,
                    EmploymentLevel.Cadet
                ],
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-1-hours-option-1a-contract",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal/TSI Employment Hours (Option 1a - Contract)",
                targetValue: 4
            },
            {
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-1-hours-option-1b-organisation",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal/TSI Employment Hours (Option 1b - Organisation)",
                targetValue: 3
            },
            // Spend
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.SupplyNationRegistered
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "nat-1-spend-option-2a-contract",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal/TSI Business Spend (Option 2a - Contract)",
                targetValue: 4
            },
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.SupplyNationRegistered
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                id: "nat-1-spend-option-2b-organisation",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal/TSI Business Spend (Option 2b - Organisation)",
                targetValue: 3
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-1-document-indigenous-participation-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Indigenous Participation Plan (IPP Plan)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-1-document-quarterly-mmr-compliance-reports",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly MMR Compliance Reports (via IPPRS)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-1-document-final-reconciliation-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Final Reconciliation Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-1-document-commonwealth-ipp-indigenous-participation-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Commonwealth IPP Indigenous Participation Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-1-document-quarterly-ipprs-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly IPPRS Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "MSA",
        frameworkId: FrameworkId.NAT_2,
        frameworkName: "Commonwealth Modern Slavery Act 2018",
        targets: [
            // Documents
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-2-document-modern-slavery-statement",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Modern Slavery Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-2-document-modern-slavery-contract-clause",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Modern Slavery Contract Clause (C.C.22)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-2-document-supply-chain-due-diligence-documentation",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Supply Chain Due Diligence Documentation"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "ESP",
        frameworkId: FrameworkId.NAT_3,
        frameworkName: "Commonwealth Employment and Skills Policy (ESP) 2024",
        targets: [
            // Environmental — Commonwealth ESP 2024. Report-only throughout: the
            // policy asks for the figure, not for a threshold.
            //
            // The two materials-wide entries are `generatable: false`. "All materials"
            // spans kg, tonnes and cubic metres, and a target may not span units
            // (`environmentalTargetScopeIsCoherent`) — so there is no honest target
            // to generate. `EnvironmentalPolicyAlert` names them on the
            // Environmental step and the project scopes its own.
            {
                generatable: false,
                id: "nat-3-environmental-esp-recycled-content",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "ESP - Recycled content materials used",
                targetType: EnvironmentalTargetType.Materials
            },
            {
                generatable: false,
                id: "nat-3-environmental-esp-low-embodied-emissions",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "ESP - Low embodied emissions materials used",
                targetType: EnvironmentalTargetType.Materials
            },
            {
                category: EnvironmentalCategory.ConstructionDemolitionWaste,
                id: "nat-3-environmental-esp-waste-diverted",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "ESP - Waste diverted from landfill",
                targetType: EnvironmentalTargetType.Waste
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-3-document-environmental-compliance-evidence",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Compliance Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-3-document-environmental-management-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management Plan (EMP)"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-3-document-esp-annual-progress-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "ESP Annual Progress Data"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "BAP",
        frameworkId: FrameworkId.NAT_4,
        frameworkName: "Commonwealth Buy Australian Plan",
        targets: [
            // Local content
            {
                entityTypeLabel: "Australian Business",
                id: "nat-4-local-content-australian-business-spend",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "Australian Business Spend"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-4-document-broader-economic-benefits-assessment",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Broader Economic Benefits Assessment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-4-document-austender-contract-notice",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "AusTender Contract Notice"
            }
        ]
    },
    // ── Green Star rating tools ──────────────────────────────────────────────
    //
    // Certification schemes: credits, points and levels live in
    // ./creditScopes.ts. Only what Catchment can evidence is mapped here.
    //
    // No Green Star tool sets a women-in-trades or First Nations spend target
    // (#77, #78). RAP and training coverage count via `coverageSources` (#32).
    {
        // Green Star Buildings v1.1 (GBCA) — new buildings.
        country: "AU",
        frameworkAbbreviation: "GSB",
        frameworkId: FrameworkId.NAT_5,
        frameworkName: "Green Star Buildings v1.1",
        targets: [
            // Hours
            {
                // Report-only: jobs by target group, reported at practical completion.
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-5-hours-by-target-group",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Labour Hours by Target Group (FTE reporting)"
            },
            // Spend
            {
                // Credit 36: 2% Credit Achievement, 4% Exceptional. Migrant-group
                // suppliers (p.267) have no `SocialSpendEntityType`, so cannot count.
                certifications: verifiedSupplierCertifications,
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: verifiedSupplierEntityTypes,
                id: "nat-5-spend-verified-supplier",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Verified Supplier Spend",
                targetValue: 2
            },
            // Environmental
            // Credit 2: C&D waste diverted by mass. See wasteDiversion.
            wasteDiversion("nat-5-environmental-waste-diverted", 90),
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-proc-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Strategy"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-contracts",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "contracts-targets-written-in",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Contracts Showing the Targets Written In"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-5-document-supplier-certs",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Supplier Certification Records"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-rap",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "rap-endorsement",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "RAP + Reconciliation Australia Endorsement"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-5-document-rap-public-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Public RAP Progress Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-facilities",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Gender-Inclusive Facilities and PPE Evidence"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-5-document-training-register",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "training-attendance-register",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Training Attendance Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-iso20400",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "iso20400-procurement-strategy",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan (ISO 20400) + Risk Assessment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-proc-tender-extracts",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Tender Extracts Incorporating the Responsible Procurement Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-5-document-proc-impact-reporting",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Impact and Data Reporting"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-cultural-training",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "First Nations Cultural Competency Training Record"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-site-conduct-policy",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Anti-Discrimination, Racism and Bullying Policy"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-5-document-health-programs",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Physical and Mental Health Programs Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-program-evaluation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Program Evaluation Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-ems",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management System (certified over $10m)"
            },
            {
                // Each head contractor has its own EMP (p.46), so this is per package.
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-emp",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Construction Environmental Management Plan"
            },
            {
                // Two site audits per 12 months of construction (p.50).
                frequency: PolicyFrequency.HalfYearly,
                id: "nat-5-document-emp-audits",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "EMP Site Audit Reports"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-5-document-sustainability-training",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Sustainability Training Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-waste-verification",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Compliance Verification Summary"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-5-document-cumulative-waste",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Cumulative Waste Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-waste-disclosure",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Disclosure Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-needs-analysis",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Needs Analysis Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-5-document-site-leadership",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "On-site Leadership Team Empowerment Approach"
            }
        ]
    },
    {
        // Green Star Communities v2 (GBCA) — precincts and neighbourhoods.
        country: "AU",
        frameworkAbbreviation: "GSC",
        frameworkId: FrameworkId.NAT_6,
        frameworkName: "Green Star Communities v2",
        targets: [
            // Hours
            {
                // Credit 36. Report-only: the credit counts initiatives, not a share.
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-6-hours-by-target-group",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Labour Hours by Target Group (FTE reporting)"
            },
            // Spend
            {
                // Credit 36. No threshold: Communities v2 sets no contract-value share.
                certifications: verifiedSupplierCertifications,
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: verifiedSupplierEntityTypes,
                id: "nat-6-spend-verified-supplier",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Verified Supplier Spend"
            },
            // Environmental
            // Credit 2: C&D waste diverted by mass. See wasteDiversion.
            wasteDiversion("nat-6-environmental-waste-diverted", 90),
            // Documents
            // Credit 2, Responsible Construction Practices
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-ems",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management System Certificate"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-emp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Construction Environmental Management Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-waste-disclosure",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Disclosure Statement"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-6-document-sustainability-training",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Sustainability Training Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-waste-verification",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Compliance Verification Summary"
            },
            // Credit 3, Responsible Procurement
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-iso20400",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "iso20400-procurement-strategy",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan (ISO 20400) + Risk Assessment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-hazardous-materials",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Hazardous Materials Survey and Management Process"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-procurement-plan-extended",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan Extended to 20 Items"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-circularity",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Circularity Measurement of Key Items (ISO 59020)"
            },
            // Credit 33, First Nations Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-cultural-training",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "First Nations Cultural Competency Training Record"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-site-analysis",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "First Nations Site Analysis"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-engagement-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "First Nations Engagement and Inclusion Plan"
            },
            // Credit 35, Inclusive Site Practices
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-facilities",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Inclusive Facilities and PPE Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-site-conduct-policy",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Diversity, Conduct and Inclusion Policies and Training"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-needs-analysis",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Needs Analysis Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-health-programs",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Physical and Mental Health Programs Evidence"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-6-document-program-evaluation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Program Evaluation Report"
            },
            // Credit 36, Procurement and Workforce Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-proc-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-contracts",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "contracts-targets-written-in",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Contracts Showing the Targets Written In"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-6-document-supplier-certs",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Supplier Certification Records"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-6-document-initiatives",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Procurement and Workforce Initiatives Report"
            },
            // Credit 37, Economic Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-6-document-economic-inclusion-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Economic Inclusion Program"
            }
        ]
    },
    {
        // Green Star Fitouts v1 (GBCA) — interior fitouts.
        country: "AU",
        frameworkAbbreviation: "GSF",
        frameworkId: FrameworkId.NAT_7,
        frameworkName: "Green Star Fitouts v1",
        targets: [
            // Spend
            {
                // Credit 32. 1% of contract value, at both levels.
                certifications: verifiedSupplierCertifications,
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: verifiedSupplierEntityTypes,
                id: "nat-7-spend-verified-supplier",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Verified Supplier Spend",
                targetValue: 1
            },
            // Environmental
            {
                // Credit 8. Report-only: the credit counts VOLUME (m³) and C&D waste
                // is in tonnes, so nothing here is the credit's figure (#104).
                category: EnvironmentalCategory.ConstructionDemolitionWaste,
                id: "nat-7-environmental-waste-diverted",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "C&D Waste (Material Outflows)",
                targetType: EnvironmentalTargetType.Waste
            },
            // Documents
            // Credit 8, Responsible Material Management
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-material-outflows",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Material Outflows Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-comprehensive-reporting",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Material Outflows Forecast vs Actual (Comprehensive Reporting)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-onsite-separation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "On-site Material Separation Evidence"
            },
            // Credit 10, Responsible Procurement
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-iso20400",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "iso20400-procurement-strategy",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan (ISO 20400) + Risk Assessment"
            },
            // Credit 29, Construction Site Culture
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-facilities",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Inclusive Facilities and PPE Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-site-conduct-policy",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "On-site Conduct, Drug and Alcohol, and Mental Health Policies"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-7-document-health-programs",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Physical and Mental Health Programs Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-program-evaluation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Program Evaluation Report"
            },
            // Credit 30, First Nations Equity
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-cultural-training",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "First Nations Cultural Competency Training Record"
            },
            // Credit Achievement takes ONE of two routes (pp.180-182), so one
            // requirement carries both; a project files whichever it took.
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-rap-deliverables",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "RAP Deliverables Implemented, or First Nations Design Engagement and Outcomes"
            },
            // Credit 32, Procurement and Workforce Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-7-document-economic-support",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Economic Support Records (1% of Contract Value)"
            }
        ]
    },
    {
        // Green Star Design & As Built v1.2 (GBCA), legacy (registrations closed
        // 17 Dec 2021). No core social credit: they arrive as Innovation 30E
        // substitutions plus the 30D RAP Challenge (#31).
        country: "AU",
        frameworkAbbreviation: "GSD",
        frameworkId: FrameworkId.NAT_10,
        frameworkName: "Green Star Design & As Built v1.2",
        targets: [
            // Hours
            {
                // Report-only. Jobs by target group for the P&WI substitution.
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-10-hours-by-target-group",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Labour Hours by Target Group (FTE reporting)"
            },
            // Spend
            {
                // P&WI via 30E. Level-sensitive: 2% / 4%, as Green Star Buildings.
                certifications: verifiedSupplierCertifications,
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: verifiedSupplierEntityTypes,
                id: "nat-10-spend-verified-supplier",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Verified Supplier Spend",
                targetValue: 2
            },
            // Environmental
            // Credit 22B: C&D waste diverted by mass. See wasteDiversion.
            wasteDiversion("nat-10-environmental-waste-diverted", 90),
            {
                // Credit 20.1: 95% of steel by mass. Report-only and parked until an
                // eco-label share can be weighted by mass (#87).
                category: EnvironmentalCategory.MaterialsSteel,
                id: "nat-10-environmental-steel",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "Structural and Reinforcing Steel",
                targetType: EnvironmentalTargetType.Materials
            },
            {
                // Credit 20.2: 95% of timber by cost FSC- or PEFC-certified, or reused.
                // Reuse has no materials field, so the share can only under-read.
                category: EnvironmentalCategory.MaterialsWoodTimber,
                ecoLabels: [
                    EcoLabel.IsealSupplyChainStewardship,
                    EcoLabel.PefcChainOfCustody
                ],
                id: "nat-10-environmental-timber",
                measure: EnvironmentalTargetMeasure.Value,
                measuredBy: EnvironmentalTargetMeasuredBy.EcoLabelShare,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.Percentage,
                targetName: "Timber Certified FSC or PEFC (by cost)",
                targetType: EnvironmentalTargetType.Materials,
                targetValue: 95
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-emp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Construction Environmental Management Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-ems",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management System + Audit Report"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-10-document-wellbeing-register",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Staff Wellbeing Program Register"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-10-document-sustainability-training",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Sustainability Training Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-waste-reporting",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Verification Summary or Disclosure Statement"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-10-document-cumulative-waste",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Cumulative Waste Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-steel-certificates",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Steel Maker Certificates"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-timber-chain-of-custody",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Timber Certification and Chain of Custody"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-pvc-declarations",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "PVC-Free Product Declarations"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-low-voc",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Low-VOC Paints, Adhesives, Sealants and Carpets Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-low-formaldehyde",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Low-Formaldehyde Engineered Wood Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-proc-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Strategy"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-contracts",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "contracts-targets-written-in",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Contracts Showing the Targets Written In"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-10-document-supplier-certs",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Supplier Certification Records"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-facilities",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Gender-Inclusive Facilities and PPE Evidence"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-10-document-training-register",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "training-attendance-register",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Training Attendance Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-iso20400",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "iso20400-procurement-strategy",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan (ISO 20400) + Risk Assessment"
            },
            // Credit 30D, the Reconciliation Action Plan Innovation Challenge.
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-10-document-rap",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "rap-endorsement",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "RAP + Reconciliation Australia Endorsement"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-10-document-rap-public-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Public RAP Progress Report"
            }
        ]
    },
    {
        // Green Star Buildings v1 (GBCA), superseded by v1.1 (NAT_5) but still
        // certifying. v1's credit numbering.
        country: "AU",
        frameworkAbbreviation: "GSB1",
        frameworkId: FrameworkId.NAT_11,
        frameworkName: "Green Star Buildings v1",
        targets: [
            // Hours
            {
                // Credit 33. Report-only: workforce targets count toward the 2% in
                // dollars, which Catchment does not convert yet.
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                id: "nat-11-hours-by-target-group",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Labour Hours by Target Group (FTE reporting)"
            },
            // Spend
            {
                // Credit 33: 2% Credit Achievement, 4% Exceptional. No stepped points.
                certifications: verifiedSupplierCertifications,
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: verifiedSupplierEntityTypes,
                id: "nat-11-spend-verified-supplier",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Verified Supplier Spend",
                targetValue: 2
            },
            // Environmental
            // Credit 2: C&D waste diverted by mass. See wasteDiversion.
            wasteDiversion("nat-11-environmental-waste-diverted", 90),
            // Documents
            // Credit 2, Responsible Construction
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-ems",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "EMS Certificate (ISO 14001) + Auditor Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-emp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management Plan + Demolition Drawings"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-11-document-cumulative-waste",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Cumulative Waste Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-waste-disclosure",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Disclosure Statement"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-11-document-sustainability-training",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Sustainability Training Materials + Attendance Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-waste-verification",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Waste Contractor Compliance Verification Summaries"
            },
            // Credit 31, Inclusive Construction Practices
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-site-conduct-policy",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Discrimination, Racism and Bullying Policy Extracts"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-facilities",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Gender-Inclusive Facilities Drawings or Images"
            },
            {
                // PPE is part of the on-site facilities Minimum Expectation (p.197);
                // the evidence is the types available and their purchase (p.198).
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-ppe",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Gender-Specific PPE Types and Purchase Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-lead-team-diversity",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Lead Team Diversity Evidence"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-11-document-training-register",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "training-attendance-register",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Training Attendance Register"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-needs-analysis",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Needs Analysis Report"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-11-document-health-programs",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Physical and Mental Health Programs Evidence"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-program-evaluation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Health Program Evaluation Report"
            },
            // Credit 32, Indigenous Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-rap",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "rap-endorsement",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "RAP + Reconciliation Australia Endorsement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-rap-working-group",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Evidence of a Project Team Member on the RAP Working Group"
            },
            {
                frequency: PolicyFrequency.Annually,
                id: "nat-11-document-rap-public-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Public RAP Progress Report"
            },
            {
                // The report on the outcomes of the project's RAP (p.202). The 90%
                // is counted through credit 32's coverage source (issue #32).
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-rap-outcomes",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "RAP Outcomes Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-rap-link",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Outcomes Document Linking the Project to the Organisational RAP"
            },
            // Credit 5, Responsible Procurement
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-iso20400",
                isProjectLevelPolicy: true,
                sharedRequirementKey: "iso20400-procurement-strategy",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Plan (ISO 20400) + Risk Assessment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-proc-tender-extracts",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Tender Extracts Incorporating the Responsible Procurement Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nat-11-document-proc-impact-reporting",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Responsible Procurement Impact and Data Reporting"
            },
            // Credit 33, Procurement and Workforce Inclusion
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-proc-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Strategy"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-contracts-workforce",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Workforce Targets in Main and Sub-Contracts"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nat-11-document-contracts",
                isProjectLevelPolicy: false,
                sharedRequirementKey: "contracts-targets-written-in",
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Targets in Main and Sub-Contracts"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "nat-11-document-supplier-certs",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Supplier Certification Records"
            }
        ]
    },
    //   // ── IS rating tools (parked) ─────────────────────────────────────────────
    //   //
    //   // Still parked: every IS credit code, name and number below is wrong
    //   // (issue #27). Do not revive without ISC access.
    //   {
    //     // IS Rating Design & As Built v2.1 (ISC ANZ) — infrastructure design and
    //     // construction. Ratings: Bronze / Silver / Gold / Excellent / Leading.
    //     // Credits score at Level 1 / 2 / 3 — NOT the Green Star level model. The
    //     // two are kept apart in ./creditScopes.ts, where each scheme declares its
    //     // own levels rather than sharing one enum.
    //     country: "AU",
    //     frameworkId: FrameworkId.NAT_8,
    //     frameworkName: "IS Rating Design & As Built v2.1",
    //     targets: [
    //       // Hours
    //       // ABSENT: "IS Indigenous Participation (RAP) Attainment" — see above.
    //       // ABSENT: "IS Workforce Training Coverage" — see above.
    //       {
    //         // Report-only. See NAT_6.
    //         factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
    //         id: "nat-8-hours-fte-by-group",
    //         targetCategory: FrameworkTargetCategory.Hours,
    //         targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
    //         targetMetricType: ProjectLabourHourCommitmentType.Ratio,
    //         targetName: "IS FTE Jobs by Group (report-only)"
    //       },
    //       {
    //         // Unlike Green Star, the IS map states the figure, so it is seeded.
    //         factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
    //         gender: [Gender.Female],
    //         id: "nat-8-hours-women-in-trades",
    //         occupationType: [OccupationType.TradeRole],
    //         targetCategory: FrameworkTargetCategory.Hours,
    //         targetDenominator: LabourHourTargetDenominator.TotalHoursTrade,
    //         targetMetricType: ProjectLabourHourCommitmentType.Ratio,
    //         targetName: "IS Women in Trade Roles",
    //         targetValue: 4
    //       },
    //
    //       // Spend
    //       {
    //         // Level-sensitive: 1% at Level 1, 2% at Level 2 (the stretch worth an
    //         // extra 2 points). Entity types and certifications match the Green
    //         // Star list on purpose, so one spend line can satisfy both scorecards.
    //         certifications: verifiedSupplierCertifications,
    //         commitmentType:
    //           SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
    //         entityTypes: verifiedSupplierEntityTypes,
    //         id: "nat-8-spend-verified-supplier",
    //         targetCategory: FrameworkTargetCategory.Spend,
    //         targetName: "IS Social Procurement (verified suppliers)",
    //         targetValue: 1
    //       },
    //
    //       // Documents
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-sustainable-procurement-strategy-iso-20400",
    //         isProjectLevelPolicy: true,
    //         sharedRequirementKey: "iso20400-procurement-strategy",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Sustainable Procurement Strategy (ISO 20400 aligned)"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-contracts-showing-targets-written-in",
    //         isProjectLevelPolicy: false,
    //         sharedRequirementKey: "contracts-targets-written-in",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Contracts Showing Targets Written In"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-rap-indigenous-participation-plan-endorsement",
    //         isProjectLevelPolicy: true,
    //         sharedRequirementKey: "rap-endorsement",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "RAP / Indigenous Participation Plan + Endorsement"
    //       },
    //       {
    //         frequency: PolicyFrequency.Monthly,
    //         id: "nat-8-document-rap-actions-log",
    //         isProjectLevelPolicy: false,
    //         sharedRequirementKey: "rap-actions-log",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "RAP Actions Log"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-workforce-development-strategy",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Workforce Development Strategy"
    //       },
    //       {
    //         frequency: PolicyFrequency.Monthly,
    //         id: "nat-8-document-sustainability-induction-register",
    //         isProjectLevelPolicy: false,
    //         sharedRequirementKey: "sustainability-induction-register",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Sustainability Induction Register"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-diversity-inclusion-plan",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Diversity & Inclusion Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-energy-carbon-management-plan",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Energy & Carbon Management Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-waste-management-plan",
    //         isProjectLevelPolicy: true,
    //         sharedRequirementKey: "waste-management-plan",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Waste Management Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-8-document-stakeholder-engagement-plan",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Stakeholder Engagement Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.Quarterly,
    //         id: "nat-8-document-community-feedback-register",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Community Feedback Register"
    //       }
    //
    //       // Environmental (disabled — see the note on the first commented block).
    //       // Construction energy & water tracking (report-only), and the IS waste
    //       // diversion rate (>= 90% diverted). Both need a threshold on the entity.
    //     ]
    //   },
    //   {
    //     // IS Rating Operations v2.1 (ISC ANZ) — operating assets. No construction
    //     // workforce, so no Indigenous participation or diversity credit and no
    //     // trade-hours target. Ratings: Bronze / Silver / Gold / Excellent /
    //     // Leading.
    //     country: "AU",
    //     frameworkId: FrameworkId.NAT_9,
    //     frameworkName: "IS Rating Operations v2.1",
    //     targets: [
    //       // Hours
    //       // ABSENT: "IS Workforce Training Coverage" — see the note above.
    //       {
    //         // Report-only. See NAT_6.
    //         factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
    //         id: "nat-9-hours-fte-by-group",
    //         targetCategory: FrameworkTargetCategory.Hours,
    //         targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
    //         targetMetricType: ProjectLabourHourCommitmentType.Ratio,
    //         targetName: "IS FTE Jobs by Group (report-only)"
    //       },
    //
    //       // Spend
    //       {
    //         // Level-sensitive: 1% at Level 1, 2% at Level 2. Measured against the
    //         // operating contract value.
    //         certifications: verifiedSupplierCertifications,
    //         commitmentType:
    //           SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
    //         entityTypes: verifiedSupplierEntityTypes,
    //         id: "nat-9-spend-verified-supplier",
    //         targetCategory: FrameworkTargetCategory.Spend,
    //         targetName: "IS Social Procurement (verified suppliers)",
    //         targetValue: 1
    //       },
    //
    //       // Documents
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-9-document-sustainable-procurement-strategy-iso-20400",
    //         isProjectLevelPolicy: true,
    //         sharedRequirementKey: "iso20400-procurement-strategy",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Sustainable Procurement Strategy (ISO 20400 aligned)"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-9-document-workforce-development-strategy",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Workforce Development Strategy"
    //       },
    //       {
    //         frequency: PolicyFrequency.Monthly,
    //         id: "nat-9-document-sustainability-induction-register",
    //         isProjectLevelPolicy: false,
    //         sharedRequirementKey: "sustainability-induction-register",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Sustainability Induction Register"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-9-document-energy-carbon-management-plan",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Energy & Carbon Management Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-9-document-waste-management-plan",
    //         isProjectLevelPolicy: true,
    //         sharedRequirementKey: "waste-management-plan",
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Waste Management Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.OneTime,
    //         id: "nat-9-document-stakeholder-engagement-plan",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Stakeholder Engagement Plan"
    //       },
    //       {
    //         frequency: PolicyFrequency.Quarterly,
    //         id: "nat-9-document-community-feedback-register",
    //         isProjectLevelPolicy: true,
    //         targetCategory: FrameworkTargetCategory.Documents,
    //         targetName: "Community Feedback Register"
    //       }
    //
    //       // Environmental (disabled — see the note on the first commented block).
    //       // Operational energy & water tracking, and the operational waste
    //       // diversion rate (>= 80% diverted).
    //     ]
    //   },
    {
        country: "AU",
        frameworkAbbreviation: "VSPF",
        frameworkId: FrameworkId.VIC_1,
        frameworkName: "Victorian Social Procurement Framework (SPF)",
        state: "VIC",
        targets: [
            // Hours
            {
                id: "vic-1-hours-priority-jobseeker-employment-hours",
                socialFactors: [
                    EmployeeSocialFactor.JobSeekersRegisteredWithJobsVictoriaMentorService,
                    EmployeeSocialFactor.LongTermUnemployed,
                    EmployeeSocialFactor.DisengagedYouth
                ],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Priority Jobseeker Employment Hours",
                targetValue: 1
            },
            {
                id: "vic-1-hours-victorian-aboriginal-employment-hours",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Victorian Aboriginal Employment Hours",
                targetValue: 1
            },
            {
                id: "vic-1-hours-australian-disability-employment-hours",
                socialFactors: [EmployeeSocialFactor.PersonWithDisability],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Australian Disability Employment Hours",
                targetValue: 1
            },
            // Spend
            {
                certifications: indigenousBusinessCertifications,
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "vic-1-spend-aboriginal-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Business Spend",
                targetValue: 1
            },
            {
                certifications: [SocialSpendCertificate.SocialTraders],
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: [SocialSpendEntityType.SocialEnterprise],
                id: "vic-1-spend-social-enterprise-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Social Enterprise Spend",
                targetValue: 1
            },
            {
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: [SocialSpendEntityType.DisabilityEnterprise],
                id: "vic-1-spend-disability-enterprise-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Disability Enterprise Spend",
                targetValue: 0.5
            },
            {
                commitmentType: SocialCommitmentTypeSpend.SocialDisabilityEnterpriseSpend,
                entityTypes: [
                    SocialSpendEntityType.IndigenousEnterprise,
                    SocialSpendEntityType.SocialEnterprise,
                    SocialSpendEntityType.DisabilityEnterprise
                ],
                id: "vic-1-spend-total-social-benefit-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Total Social Benefit Spend",
                targetValue: 2
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-1-document-social-procurement-commitment-proposal",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Commitment Proposal"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-1-document-social-procurement-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Social Procurement Plan"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-1-document-monthly-spend-hours-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Monthly Spend & Hours Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "VIPP",
        frameworkId: FrameworkId.VIC_2,
        frameworkName: "Local Jobs First - Victorian Industry Participation Policy (VIPP)",
        state: "VIC",
        targets: [
            // Hours
            {
                employmentLevels: vicAtcEmploymentLevels,
                id: "vic-2-hours-local-ftes-created-retained",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Headcount,
                targetName: "Local FTEs Created & Retained"
            },
            // Local content
            {
                id: "vic-2-local-content-standard-projects-building",
                localContentType: LocalContentType.Building,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Building"
            },
            {
                id: "vic-2-local-content-standard-projects-labour",
                localContentType: LocalContentType.Labour,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Labour"
            },
            {
                id: "vic-2-local-content-standard-projects-infrastructure",
                localContentType: LocalContentType.Infrastructure,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Infrastructure"
            },
            {
                id: "vic-2-local-content-standard-projects-product-materials",
                localContentType: LocalContentType.SupplyOfProductAndMaterials,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Supply of Product & Materials"
            },
            {
                id: "vic-2-local-content-standard-projects-steel",
                localContentType: LocalContentType.SteelRelatedItems,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Steel Related Items"
            },
            {
                id: "vic-2-local-content-standard-projects-specialist-equipment",
                localContentType: LocalContentType.SpecialistEquipment,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Specialist Equipment"
            },
            {
                id: "vic-2-local-content-standard-projects-plant-equipment",
                localContentType: LocalContentType.PlantAndEquipment,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Plant & Equipment"
            },
            {
                id: "vic-2-local-content-standard-projects-other",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Standard Projects - Other"
            },
            {
                id: "vic-2-local-content-strategic-construction-building",
                localContentType: LocalContentType.Building,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Construction - Building"
            },
            {
                id: "vic-2-local-content-strategic-construction-infrastructure",
                localContentType: LocalContentType.Infrastructure,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Construction - Infrastructure"
            },
            {
                id: "vic-2-local-content-strategic-construction-labour",
                localContentType: LocalContentType.Labour,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Construction - Labour"
            },
            {
                id: "vic-2-local-content-strategic-construction-steel",
                localContentType: LocalContentType.SteelRelatedItems,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Construction - Steel Related Items"
            },
            {
                id: "vic-2-local-content-strategic-construction-specialist-equipment",
                localContentType: LocalContentType.SpecialistEquipment,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Construction - Specialist Equipment"
            },
            {
                id: "vic-2-local-content-strategic-services-operations-maintenance",
                localContentType: LocalContentType.OperationsMaintenance,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Services - Operations & Maintenance"
            },
            {
                id: "vic-2-local-content-strategic-services-specialist-equipment",
                localContentType: LocalContentType.SpecialistEquipment,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Services - Specialist Equipment"
            },
            {
                id: "vic-2-local-content-strategic-services-other",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "ANZ Local Content - Strategic Services - Other"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-2-document-lidp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Local Industry Development Plan (LIDP)"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-2-document-monthly-local-content-jobs-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Monthly Local Content & Jobs Data"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-2-document-statutory-declaration",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Statutory Declaration"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "MPSG",
        frameworkId: FrameworkId.VIC_3,
        frameworkName: "Major Projects Skills Guarantee (MPSG)",
        state: "VIC",
        targets: [
            // Hours
            {
                employmentLevels: vicAtcEmploymentLevels,
                id: "vic-3-hours-atc-hours-apprentices-trainees-cadets",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "ATC Hours - Apprentices + Trainees + Cadets",
                targetValue: 10
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-3-document-estimated-labour-hours-lidp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Estimated Labour Hours (in LIDP)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-3-document-training-contract-per-worker",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Training Contract per Worker"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-3-document-atc-hours-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "ATC Hours Data"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-3-document-statutory-declaration-final-atc-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Statutory Declaration + Final ATC Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "BEP",
        frameworkId: FrameworkId.VIC_4,
        frameworkName: "Building Equality Policy (BEP)",
        state: "VIC",
        targets: [
            // Hours
            {
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                gender: [Gender.Female],
                id: "vic-4-hours-women-in-trade-roles",
                occupationType: [OccupationType.TradeRole],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalHoursTrade,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Women in Trade Roles",
                targetValue: 3
            },
            {
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                gender: [Gender.Female],
                id: "vic-4-hours-women-in-non-trade-roles",
                occupationType: [OccupationType.NonTradePosition],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalHoursNonTrade,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Women in Non-Trade Roles",
                targetValue: 7
            },
            {
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                gender: [Gender.Female],
                id: "vic-4-hours-women-in-management-roles",
                occupationType: [
                    OccupationType.ManagementSupervisorSpecialistLabourRoles
                ],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalHoursManagement,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Women in Management Roles",
                targetValue: 35
            },
            {
                employmentLevels: vicAtcEmploymentLevels,
                factorCountingMethod: LabourHourFactorCountingMethod.AllFactorsCombined,
                gender: [Gender.Female],
                id: "vic-4-hours-women-atc-roles",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Women ATC Roles",
                targetValue: 4
            },
            // Documents
            {
                frequency: PolicyFrequency.Annually,
                id: "vic-4-document-organisation-wide-geap",
                isProjectLevelPolicy: true,
                policyType: PolicyType.EntityLevelGenderEqualityActionPlan,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Organisation-Wide GEAP"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-4-document-project-specific-geap",
                isProjectLevelPolicy: true,
                policyType: PolicyType.ProjectLevelGenderEqualityActionPlan,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Project-Specific GEAP"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-4-document-bep-hours-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "BEP Hours Data"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "RFP",
        frameworkId: FrameworkId.VIC_5,
        frameworkName: "Recycled First Policy",
        state: "VIC",
        targets: [
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-recycled-first-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Recycled First Plan"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-5-document-recycled-first-reporting-table",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Recycled First Reporting Table"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-5-document-materials-docket-register",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Materials Docket Register"
            },
            // One method statement per material stream. These are the evidence half
            // of the recycled-content percentages below: the percentages themselves
            // are environmental targets and stay disabled, so today the policy is
            // tracked by its documents alone.
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-aggregates-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aggregates Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-rap-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Reclaimed Asphalt Pavement (RAP) Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-crushed-brick-fill-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Crushed Brick & Fill Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-recycled-glass-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Recycled Glass Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-spoil-reuse-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Spoil Reuse Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-steel-ballast-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Steel & Ballast Method Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-5-document-recycled-plastics-method-statement",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Recycled Plastics Method Statement"
            },
            // Environmental — Recycled First. Materials-wide, so `generatable: false` —
            // see the note on NAT-3.
            {
                generatable: false,
                id: "vic-5-environmental-recycled-reused-materials",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "Recycled First - Recycled & reused materials",
                targetType: EnvironmentalTargetType.Materials
            },
            {
                category: EnvironmentalCategory.SpoilFillSoilMaterial,
                id: "vic-5-environmental-site-won-material-reused",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "Recycled First - Site-won material reused",
                targetType: EnvironmentalTargetType.Waste
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "AES",
        frameworkId: FrameworkId.VIC_6,
        frameworkName: "Aboriginal Employment & Spend",
        state: "VIC",
        targets: [
            // Hours
            {
                id: "vic-6-hours-aboriginal-employment-hours",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours",
                targetValue: 2.5
            },
            // Spend
            {
                certifications: indigenousBusinessCertifications,
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "vic-6-spend-aboriginal-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Business Spend",
                targetValue: 1
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-6-document-aboriginal-employment-spend-strategy",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aboriginal Employment & Spend Strategy"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-6-document-monthly-aboriginal-employment-spend-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Monthly Aboriginal Employment & Spend Data"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "GS",
        frameworkId: FrameworkId.VIC_7,
        frameworkName: "Green Star / Environmental Reporting",
        state: "VIC",
        targets: [
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-7-document-environmental-management-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management Plan (EMP)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "vic-7-document-climate-risk-assessment",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Climate Risk Assessment"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "vic-7-document-ghg-energy-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "GHG & Energy Data"
            }
            // Environmental — out of scope. VIC-7 is absent from the reviewed policy
            // source, so these four were never validated against it. Left commented
            // rather than authored by inference. Written in the Fuel Type taxonomy:
            // a fuel target names its leaf, since a whole fuel family is no longer a
            // category of its own, so "Gas" would have to be chosen as natural gas,
            // LPG or both before this could be enabled.
            // {
            // category: EnvironmentalCategory.EnergyFuelType,
            // id: "vic-7-environmental-energy-diesel",
            // subCategory: EnvironmentalSubCategory.Diesel,
            // targetCategory: FrameworkTargetCategory.Environmental,
            // targetName: "Energy - Diesel",
            // targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            // },
            // {
            // category: EnvironmentalCategory.EnergyFuelType,
            // id: "vic-7-environmental-energy-gas",
            // subCategory: EnvironmentalSubCategory.GasNatural,
            // targetCategory: FrameworkTargetCategory.Environmental,
            // targetName: "Energy - Gas",
            // targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            // },
            // {
            // category: EnvironmentalCategory.EnergyFuelType,
            // id: "vic-7-environmental-energy-petrol",
            // subCategory: EnvironmentalSubCategory.Petrol,
            // targetCategory: FrameworkTargetCategory.Environmental,
            // targetName: "Energy - Petrol",
            // targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            // },
            // {
            // category: EnvironmentalCategory.EnergyElectricity,
            // id: "vic-7-environmental-energy-electricity",
            // targetCategory: FrameworkTargetCategory.Environmental,
            // targetName: "Energy - Electricity",
            // targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            // }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "ISLP",
        frameworkId: FrameworkId.NSW_1,
        frameworkName: "Infrastructure Skills Legacy Program (ISLP)",
        state: "NSW",
        targets: [
            // Hours
            {
                employmentLevels: [EmploymentLevel.Apprentice],
                id: "nsw-1-hours-apprentice-hours",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Apprentice Hours",
                targetValue: 20
            },
            {
                employmentLevels: [
                    EmploymentLevel.Apprentice,
                    EmploymentLevel.Trainee,
                    EmploymentLevel.Upskilling
                ],
                id: "nsw-1-hours-learning-worker-hours",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Learning Worker Hours",
                targetValue: 20
            },
            {
                gender: [Gender.Female],
                id: "nsw-1-hours-women-in-trade-hours",
                occupationType: [OccupationType.TradeRole],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Women in Trade Hours",
                targetValue: 2
            },
            {
                id: "nsw-1-hours-under-25-hours",
                socialFactors: [EmployeeSocialFactor.YoungPeopleAgedUnder25],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Under-25 Hours",
                targetValue: 8
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-1-document-skills-training-diversity-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Skills, Training & Diversity Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nsw-1-document-quarterly-report-training-services-nsw",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly Report to Training Services NSW"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-1-document-final-completion-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Final Completion Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "APP",
        frameworkId: FrameworkId.NSW_2,
        frameworkName: "NSW Aboriginal Procurement Policy (APP)",
        state: "NSW",
        targets: [
            // Hours
            {
                id: "nsw-2-hours-aboriginal-participation-employment",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Participation - Employment",
                targetValue: 1.5
            },
            {
                id: "nsw-2-hours-aboriginal-participation-training",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Participation - Training",
                targetValue: 1.5
            },
            // Spend
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.SupplyNationRegistered
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "nsw-2-spend-aboriginal-participation-subcontracting",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Participation - Subcontracting",
                targetValue: 1.5
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-2-document-aboriginal-participation-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aboriginal Participation Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nsw-2-document-quarterly-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly Progress Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-2-document-final-reconciliation-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Final Reconciliation Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "GREP",
        frameworkId: FrameworkId.NSW_3,
        frameworkName: "NSW Government Resource Efficiency Policy (GREP)",
        state: "NSW",
        targets: [
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-3-document-nabers-energy-commitment",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "NABERS Energy Commitment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-3-document-green-star-design-as-built-registration",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Green Star Design & As-Built Registration"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-3-document-environmental-management-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management Plan (EMP)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-3-document-nabers-rating-post-completion",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "NABERS Rating (post-completion)"
            },
            // Environmental — NSW GREP.
            {
                category: EnvironmentalCategory.EnergyElectricity,
                id: "nsw-3-environmental-grep-electricity",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "GREP E1 - Electricity use",
                targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            },
            // One target per fuel: fuels are leaves of the one Fuel Type category,
            // and they span units (m3, litres, cylinders). GREP's own fuel mix is
            // electricity, natural gas and LPG.
            {
                category: EnvironmentalCategory.EnergyFuelType,
                id: "nsw-3-environmental-grep-natural-gas",
                subCategory: EnvironmentalSubCategory.GasNatural,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "GREP - Natural gas use",
                targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            },
            {
                category: EnvironmentalCategory.EnergyFuelType,
                id: "nsw-3-environmental-grep-lpg",
                subCategory: EnvironmentalSubCategory.GasLpg,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "GREP - LPG use",
                targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            },
            {
                category: EnvironmentalCategory.WaterPotable,
                id: "nsw-3-environmental-grep-potable-water",
                subCategory: EnvironmentalSubCategory.WaterThirdPartySources,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "GREP W1 - Potable water consumption",
                targetType: EnvironmentalTargetType.WaterUsage
            },
            {
                category: EnvironmentalCategory.OfficeWaste,
                id: "nsw-3-environmental-grep-office-waste",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "GREP P1 - Office waste streams",
                targetType: EnvironmentalTargetType.Waste
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "LSMT",
        frameworkId: FrameworkId.NSW_4,
        frameworkName: "NSW Local Supplier Market Testing",
        state: "NSW",
        targets: [
            // Local content
            {
                id: "nsw-4-local-content-local-supplier-market-test",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "NSW Local Supplier Market Test"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nsw-4-document-local-supplier-market-testing-statement",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Local Supplier Market Testing Statement"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "BCTP",
        frameworkId: FrameworkId.QLD_1,
        frameworkName: "QLD Building & Construction Training Policy",
        state: "QLD",
        targets: [
            // Hours
            {
                employmentLevels: [EmploymentLevel.Apprentice, EmploymentLevel.Trainee],
                id: "qld-1-hours-training-hours-standard",
                newEntrantToConstruction: true,
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Training Hours - Standard",
                targetValue: 10
            },
            {
                employmentLevels: [EmploymentLevel.Apprentice, EmploymentLevel.Trainee],
                id: "qld-1-hours-training-hours-enhanced-100m",
                newEntrantToConstruction: true,
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Training Hours - Enhanced (≥$100M)",
                targetValue: 15
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-1-document-skills-development-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Skills Development Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-1-document-tpas-registration",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "TPAS Registration"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "qld-1-document-monthly-hours-entry-tpas-portal",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Monthly Hours Entry (TPAS portal)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-1-document-practical-completion-report-via-tpas",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Practical Completion Report (via TPAS)"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "QIPP",
        frameworkId: FrameworkId.QLD_2,
        frameworkName: "QLD Indigenous Procurement Policy (QIPP)",
        state: "QLD",
        targets: [
            // Spend
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.QueenslandIndigenousBusinessNetwork,
                    SocialSpendCertificate.BuyQLDVerified
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "qld-2-spend-aboriginal-tsi-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal/TSI Business Spend",
                targetValue: 3
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-2-document-aboriginal-tsi-procurement-commitments",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aboriginal/TSI Procurement Commitments"
            },
            {
                frequency: PolicyFrequency.Monthly,
                id: "qld-2-document-spend-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Spend Data"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "CLC",
        frameworkId: FrameworkId.QLD_3,
        frameworkName: "QLD Charter for Local Content",
        state: "QLD",
        targets: [
            // Local content
            {
                id: "qld-3-local-content-qld-local-content",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "QLD Local Content"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-3-document-statement-of-intent-soi-tg",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Statement of Intent (SOI-TG)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-3-document-project-outcome-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Project Outcome Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "IEO",
        frameworkId: FrameworkId.QLD_4,
        frameworkName: "QLD Indigenous Economic Opportunities Plan (IEO)",
        state: "QLD",
        targets: [
            // Hours
            {
                id: "qld-4-hours-aboriginal-tsi-employment-hours",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal/TSI Employment Hours",
                targetValue: 10
            },
            // Spend
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.QueenslandIndigenousBusinessNetwork
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "qld-4-spend-aboriginal-tsi-business-subcontracting-building",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal/TSI Business Subcontracting - Building",
                targetValue: 3
            },
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.QueenslandIndigenousBusinessNetwork
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "qld-4-spend-aboriginal-tsi-business-subcontracting-civil",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal/TSI Business Subcontracting - Civil",
                targetValue: 1.5
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "qld-4-document-ieo-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "IEO Plan"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "IPS",
        frameworkId: FrameworkId.WA_1,
        frameworkName: "WA Industry Participation Strategy (IPS) 2024",
        state: "WA",
        targets: [
            // Local content
            {
                id: "wa-1-local-content-wa-local-content-building",
                localContentType: LocalContentType.Building,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Building"
            },
            {
                id: "wa-1-local-content-wa-local-content-labour",
                localContentType: LocalContentType.Labour,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Labour"
            },
            {
                id: "wa-1-local-content-wa-local-content-infrastructure",
                localContentType: LocalContentType.Infrastructure,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Infrastructure"
            },
            {
                id: "wa-1-local-content-wa-local-content-product-materials",
                localContentType: LocalContentType.SupplyOfProductAndMaterials,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Supply of Product & Materials"
            },
            {
                id: "wa-1-local-content-wa-local-content-steel",
                localContentType: LocalContentType.SteelRelatedItems,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Steel Related Items"
            },
            {
                id: "wa-1-local-content-wa-local-content-other",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "WA Local Content - Other"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "wa-1-document-waips-participation-plan-core-or-full",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "WAIPS Participation Plan (Core or Full)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "wa-1-document-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Progress Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "APP",
        frameworkId: FrameworkId.WA_2,
        frameworkName: "WA Aboriginal Procurement Policy (APP)",
        state: "WA",
        targets: [
            // Spend
            {
                certifications: [
                    SocialSpendCertificate.SupplyNationCertified,
                    SocialSpendCertificate.SupplyNationRegistered,
                    SocialSpendCertificate.AboriginalBusinessDirectoryWA
                ],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "wa-2-spend-aboriginal-business-spend-option-a",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Business Spend (Option A)",
                targetValue: 4
            },
            // TODO: Different regions
            // Hours
            {
                id: "wa-2-hours-aboriginal-employment-hours-option-b-perth-sw",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours (Option B) - Perth & SW",
                targetValue: 2
            },
            {
                id: "wa-2-hours-aboriginal-employment-hours-option-b-mid-regions",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours (Option B) - Mid-regions",
                targetValue: 5
            },
            {
                id: "wa-2-hours-aboriginal-employment-hours-option-b-pilbara-kimberley",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours (Option B) - Pilbara & Kimberley",
                targetValue: 10
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "wa-2-document-waips-plan-including-apr",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "WAIPS Plan (including APR)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "wa-2-document-apr-progress-reports",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "APR Progress Reports"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "wa-2-document-final-reconciliation-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Final Reconciliation Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "CTF",
        frameworkId: FrameworkId.WA_3,
        frameworkName: "WA Construction Training Fund Levy (CTF)",
        state: "WA",
        targets: [
            {
                frequency: PolicyFrequency.OneTime,
                id: "wa-3-document-ctf-levy-payment",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "CTF Levy Payment"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "wa-3-document-levy-reconciliation",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Levy Reconciliation"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "SAIP",
        frameworkId: FrameworkId.SA_1,
        frameworkName: "SA Industry Participation Policy (SAIPP) 2023",
        state: "SA",
        targets: [
            // Local content
            {
                id: "sa-1-local-content-sa-local-content-building",
                localContentType: LocalContentType.Building,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Building"
            },
            {
                id: "sa-1-local-content-sa-local-content-labour",
                localContentType: LocalContentType.Labour,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Labour"
            },
            {
                id: "sa-1-local-content-sa-local-content-infrastructure",
                localContentType: LocalContentType.Infrastructure,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Infrastructure"
            },
            {
                id: "sa-1-local-content-sa-local-content-product-materials",
                localContentType: LocalContentType.SupplyOfProductAndMaterials,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Supply of Product & Materials"
            },
            {
                id: "sa-1-local-content-sa-local-content-steel",
                localContentType: LocalContentType.SteelRelatedItems,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Steel Related Items"
            },
            {
                id: "sa-1-local-content-sa-local-content-other",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "SA Local Content - Other"
            },
            // Hours
            {
                id: "sa-1-hours-sa-employment-committed",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Headcount,
                targetName: "SA Employment Committed"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-1-document-standard-ipp-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Standard IPP Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-1-document-tailored-ipp-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Tailored IPP Plan"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "sa-1-document-ipp-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "IPP Progress Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-1-document-steel-compliance-declaration",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Steel Compliance Declaration"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "SSA",
        frameworkId: FrameworkId.SA_2,
        frameworkName: "Skilling South Australia",
        state: "SA",
        targets: [
            // Hours
            {
                factorCountingMethod: LabourHourFactorCountingMethod.AnyMatchingFactor,
                id: "sa-2-hours-nominated-group-hours",
                socialFactors: [
                    EmployeeSocialFactor.AboriginalOrTorresStraitIslander,
                    EmployeeSocialFactor.LongTermUnemployed,
                    EmployeeSocialFactor.PersonWithDisability,
                    EmployeeSocialFactor.YoungPeopleAgedUnder25
                ],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Nominated Group Hours",
                targetValue: 15
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-2-document-workforce-participation-commitments-within-ipp-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Workforce Participation Commitments (within IPP Plan)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "sa-2-document-quarterly-workforce-participation-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly Workforce Participation Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-2-document-final-workforce-participation-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Final Workforce Participation Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "AEP",
        frameworkId: FrameworkId.SA_3,
        frameworkName: "SA Aboriginal Economic Participation",
        state: "SA",
        targets: [
            // Hours
            {
                id: "sa-3-hours-aboriginal-employment-hours",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours",
                targetValue: 30
            },
            // Spend
            {
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "sa-3-spend-aboriginal-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Business Spend"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-3-document-aboriginal-participation-commitments-within-ipp-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aboriginal Participation Commitments (within IPP Plan)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "sa-3-document-quarterly-progress-data",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Quarterly Progress Data"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "GHG",
        frameworkId: FrameworkId.SA_4,
        frameworkName: "SA Department for Infrastructure and Transport (DIT) Sustainable Procurement - GHG",
        state: "SA",
        targets: [
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-4-document-emission-reduction-target-evidence-public-url",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Emission Reduction Target Evidence (public URL)"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "sa-4-document-environmental-management-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Environmental Management Plan (EMP)"
            },
            // Environmental — SA DIT GHG. The policy requires a reduction target and
            // names no number, and both entries span every category of their type.
            // So `generatable: false` on both: the project picks the scope and sets the
            // figure, prompted by `EnvironmentalPolicyAlert`.
            //
            // Both ask for EMISSIONS, and say so. They used to be plain fixed-value
            // entries, which meant they measured litres and tonnes while their own
            // names claimed to measure emissions — and while only the categories were
            // compared, ANY Energy target answered the first one, so a fuel-volume
            // target made the policy banner disappear. `measure` is what
            // `environmentalScopeCovers` now reads to tell the two apart.
            {
                emissionScopes: [
                    EmissionFactorScope.Scope1,
                    EmissionFactorScope.Scope2
                ],
                generatable: false,
                id: "sa-4-environmental-dit-scope-1-2-emissions",
                measure: EnvironmentalTargetMeasure.Emissions,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.FixedValue,
                targetName: "SA DIT - Scope 1 & 2 emissions (fuel & electricity)",
                targetType: EnvironmentalTargetType.EnergyAndFuelsAndLubricants
            },
            {
                // Embodied emissions are Scope 3 wherever they arise, and the entry
                // narrows to Materials for the source rather than for the scope.
                emissionScopes: [EmissionFactorScope.Scope3],
                generatable: false,
                id: "sa-4-environmental-dit-embodied-emissions",
                measure: EnvironmentalTargetMeasure.Emissions,
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.FixedValue,
                targetName: "SA DIT - Embodied emissions (Scope 3 source)",
                targetType: EnvironmentalTargetType.Materials
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "LIPP",
        frameworkId: FrameworkId.ACT_1,
        frameworkName: "ACT Canberra Region Local Industry Participation Policy (LIPP) & Employment and Cooperation with Territories (ECT)",
        state: "ACT",
        targets: [
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-1-document-local-industry-participation-plan-lipp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Local Industry Participation Plan (LIPP)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "act-1-document-lipp-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "LIPP Progress Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-1-document-ect-panel-soa-template",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "ECT Panel / SOA Template"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "SLJC",
        frameworkId: FrameworkId.ACT_2,
        frameworkName: "ACT Secure Local Jobs Code (SLJC)",
        state: "ACT",
        targets: [
            // Hours
            {
                id: "act-2-hours-workforce-diversity-training-hours",
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Fixed,
                targetName: "Workforce Diversity & Training Hours"
            },
            // Documents
            {
                frequency: PolicyFrequency.Annually,
                id: "act-2-document-sljc-code-certificate",
                isProjectLevelPolicy: true,
                policyType: PolicyType.FairJobsCode,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "SLJC Code Certificate"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-2-document-lrtwe-plan",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "LRTWE Plan"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-2-document-previous-lrtwe-compliance-declaration",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Previous LRTWE Compliance Declaration"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-2-document-culture-standard-conformance-docs",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Culture Standard Conformance Docs"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "ACT",
        frameworkId: FrameworkId.ACT_3,
        frameworkName: "ACT Aboriginal & Torres Strait Islander Procurement Policy",
        state: "ACT",
        targets: [
            // Spend
            {
                certifications: [SocialSpendCertificate.SupplyNationCertified],
                commitmentType: SocialCommitmentTypeSpend.ACTFirstNationsBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "act-3-spend-atsi-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "ATSI Business Spend"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "act-3-document-atsi-participation-commitments-within-ect-or-lipp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "ATSI Participation Commitments (within ECT or LIPP)"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "BLP",
        frameworkId: FrameworkId.NT_1,
        frameworkName: "NT Buy Local Plan & Buy Northern Territory Industry Participation Policy (BNTIPP)",
        state: "NT",
        targets: [
            // Local content
            {
                id: "nt-1-local-content-nt-local-content",
                localContentType: LocalContentType.Other,
                targetCategory: FrameworkTargetCategory.LocalContent,
                targetName: "NT Local Content"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nt-1-document-industry-participation-plan-ipp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Industry Participation Plan (IPP)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "nt-1-document-ipp-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "IPP Progress Report"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "IDP",
        frameworkId: FrameworkId.NT_2,
        frameworkName: "NT Aboriginal Procurement & Department of Local Industry Inclusion and Development Plan (DLI IDP)",
        state: "NT",
        targets: [
            // Spend
            {
                certifications: [SocialSpendCertificate.SupplyNationCertified],
                commitmentType: SocialCommitmentTypeSpend.AboriginalTorresStraitIslanderBusinessSpend,
                entityTypes: [SocialSpendEntityType.IndigenousEnterprise],
                id: "nt-2-spend-aboriginal-business-spend",
                targetCategory: FrameworkTargetCategory.Spend,
                targetName: "Aboriginal Business Spend"
            },
            // Hours
            {
                employmentLevels: [
                    EmploymentLevel.FullEmployment,
                    EmploymentLevel.Apprentice,
                    EmploymentLevel.Trainee
                ],
                id: "nt-2-hours-aboriginal-employment-hours",
                socialFactors: [EmployeeSocialFactor.AboriginalOrTorresStraitIslander],
                targetCategory: FrameworkTargetCategory.Hours,
                targetDenominator: LabourHourTargetDenominator.TotalWorkHours,
                targetMetricType: ProjectLabourHourCommitmentType.Ratio,
                targetName: "Aboriginal Employment Hours"
            },
            // Documents
            {
                frequency: PolicyFrequency.OneTime,
                id: "nt-2-document-aboriginal-participation-proposals",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Aboriginal Participation Proposals"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "nt-2-document-indigenous-development-plan-idp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Indigenous Development Plan (IDP)"
            }
        ]
    },
    {
        country: "AU",
        frameworkAbbreviation: "TIPP",
        frameworkId: FrameworkId.TAS_1,
        frameworkName: "Tasmania Buy Local Policy & Tasmanian Industry Participation Policy (TIPP)",
        state: "TAS",
        targets: [
            {
                frequency: PolicyFrequency.OneTime,
                id: "tas-1-document-economic-social-benefits-statement",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Economic & Social Benefits (ESB) Statement"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "tas-1-document-tasmanian-industry-participation-plan-tipp",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Tasmanian Industry Participation Plan (TIPP)"
            },
            {
                frequency: PolicyFrequency.Quarterly,
                id: "tas-1-document-tipp-progress-report",
                isProjectLevelPolicy: false,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "TIPP Progress Report"
            }
        ]
    },
    {
        // TODO: LC and Environmental targets need to be added for this framework
        country: "AU",
        frameworkAbbreviation: "WEP",
        frameworkId: FrameworkId.TAS_2,
        frameworkName: "Tasmania Wood Encouragement Policy (WEP) 2024",
        state: "TAS",
        targets: [
            {
                frequency: PolicyFrequency.OneTime,
                id: "tas-2-document-wep-clause-in-design-brief",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "WEP Clause in Design Brief"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "tas-2-document-designers-wood-consideration-report",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "Designer's Wood Consideration Report"
            },
            {
                frequency: PolicyFrequency.OneTime,
                id: "tas-2-document-wep-justification-at-tender",
                isProjectLevelPolicy: true,
                targetCategory: FrameworkTargetCategory.Documents,
                targetName: "WEP Justification at Tender"
            },
            // Environmental — Tas WEP 2024.
            {
                category: EnvironmentalCategory.MaterialsWoodTimber,
                id: "tas-2-environmental-wep-sustainable-timber",
                targetCategory: FrameworkTargetCategory.Environmental,
                targetMethod: EnvironmentalTargetMethod.ReportOnly,
                targetName: "WEP - Sustainably sourced timber used",
                targetType: EnvironmentalTargetType.Materials
            }
        ]
    }
];
